import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { HelpProvider } from "./contexts/HelpContext";
import { FieldTooltipProvider } from "./contexts/FieldTooltipContext";
import PrivateRoute from "./components/PrivateRoute";
import Layout from "./components/Layout";
import PlannerBlockGuard from "./components/PlannerBlockGuard";
import GlobalLoader from "./components/GlobalLoader";
import LoadingConnector from "./components/LoadingConnector";
import Login from "./pages/Login";
import AccountSetup from "./pages/AccountSetup";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import UserManagement from "./pages/UserManagement";
import Bridges from "./pages/Bridges";
import CorrosionDamageManagement from "./pages/CorrosionDamageManagement";
import NewSimulationTriggersManagement from "./pages/NewSimulationTriggersManagement";
import MaintenanceCycleLimitsManagement from "./pages/MaintenanceCycleLimitsManagement";
import SurfacePrepMethodsManagement from "./pages/SurfacePrepMethodsManagement";
import WashingCostsManagement from "./pages/WashingCostsManagement";
import WashRulesManagement from "./pages/WashRulesManagement";
import MaterialsMasterManagement from "./pages/MaterialsMasterManagement";
import MobilizationCostsManagement from "./pages/MobilizationCostsManagement";
import FieldApplicationLaborManagement from "./pages/FieldApplicationLaborManagement";
import SystemSettingsManagement from "./pages/SystemSettingsManagement";
import LaborByCoatManagement from "./pages/LaborByCoatManagement";
import ScenarioManagement from "./pages/ScenarioManagement";
import SpanSimulation from "./pages/SpanSimulation";
import RepairPlanning from "./pages/RepairPlanning";
import BridgeDetail from "./pages/BridgeDetail";

// New Repair Projects (Span-based)
import RepairProjects from "./pages/RepairProjects";
import CreateRepairProject from "./pages/CreateRepairProject";
import RepairProjectDetails from "./pages/RepairProjectDetails";

// Enhanced Simulation Results
import EnhancedSimulationResults from "./pages/EnhancedSimulationResults";
// New Simulation Engine (scaffold — separate from existing process)
import Help from "./pages/Help";

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <HelpProvider>
          <FieldTooltipProvider>
            <Router>
              <div className="App">
                <LoadingConnector />
                <GlobalLoader />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/account-setup" element={<AccountSetup />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <Layout />
                      </PrivateRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="profile" element={<Profile />} />
                    <Route
                      path="settings"
                      element={
                        <PlannerBlockGuard>
                          <Settings />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route path="users" element={<UserManagement />} />

                    {/* Bridge Management Routes */}
                    <Route path="bridges" element={<Bridges />} />
                    <Route path="bridges/:id" element={<BridgeDetail />} />

                    {/* Masters Management Routes */}
                    {/* <Route path="masters/components" element={<ComponentsManagement />} /> */}
                    {/* <Route path="masters/component-types" element={<ComponentTypesManagement />} /> */}

                    {/* New Master Data Routes - Planner cannot access */}
                    <Route
                      path="masters/corrosion-damage"
                      element={
                        <PlannerBlockGuard>
                          <CorrosionDamageManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    {/* Redirect old routes to unified page for backward compatibility */}
                    <Route
                      path="masters/corrosion-categories"
                      element={
                        <Navigate to="/masters/corrosion-damage" replace />
                      }
                    />
                    <Route
                      path="masters/damage-severity"
                      element={
                        <Navigate to="/masters/corrosion-damage" replace />
                      }
                    />
                    <Route
                      path="masters/corrosion-damage-combinations"
                      element={
                        <Navigate to="/masters/corrosion-damage" replace />
                      }
                    />

                    <Route
                      path="masters/surface-prep-methods"
                      element={
                        <PlannerBlockGuard>
                          <SurfacePrepMethodsManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/washing-costs"
                      element={
                        <PlannerBlockGuard>
                          <WashingCostsManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/wash-rules"
                      element={
                        <PlannerBlockGuard>
                          <WashRulesManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/materials-master"
                      element={
                        <PlannerBlockGuard>
                          <MaterialsMasterManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/mobilization-costs"
                      element={
                        <PlannerBlockGuard>
                          <MobilizationCostsManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/field-application-labor"
                      element={
                        <PlannerBlockGuard>
                          <FieldApplicationLaborManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/system-settings"
                      element={
                        <PlannerBlockGuard>
                          <SystemSettingsManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/repair-rules"
                      element={
                        <Navigate
                          to="/masters/materials-master?tab=repair-rules"
                          replace
                        />
                      }
                    />
                    <Route
                      path="masters/labor-by-coat"
                      element={
                        <PlannerBlockGuard>
                          <LaborByCoatManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/damage-split-defaults"
                      element={
                        <Navigate
                          to="/masters/corrosion-damage?tab=damage-split"
                          replace
                        />
                      }
                    />
                    <Route
                      path="masters/decay-trigger-config"
                      element={
                        <Navigate
                          to="/masters/new-simulation-triggers?tab=fallback"
                          replace
                        />
                      }
                    />
                    <Route
                      path="masters/new-simulation-triggers"
                      element={
                        <PlannerBlockGuard>
                          <NewSimulationTriggersManagement />
                        </PlannerBlockGuard>
                      }
                    />
                    <Route
                      path="masters/maintenance-cycle-limits"
                      element={
                        <PlannerBlockGuard>
                          <MaintenanceCycleLimitsManagement />
                        </PlannerBlockGuard>
                      }
                    />

                    {/* Simulation Routes */}
                    <Route path="scenarios" element={<ScenarioManagement />} />
                    <Route
                      path="span-simulation"
                      element={<SpanSimulation />}
                    />
                    <Route
                      path="enhanced-simulation-results"
                      element={<EnhancedSimulationResults />}
                    />

                    {/* Repair Planning Routes (OLD - component-based, deprecated) */}
                    <Route
                      path="repair-planning"
                      element={<RepairPlanning />}
                    />

                    {/* Repair Projects Routes (NEW - span-based) */}
                    <Route
                      path="repair-projects"
                      element={<RepairProjects />}
                    />
                    <Route
                      path="repair-projects/create"
                      element={<CreateRepairProject />}
                    />
                    <Route
                      path="repair-projects/:id/edit"
                      element={<CreateRepairProject />}
                    />
                    <Route
                      path="repair-projects/:id"
                      element={<RepairProjectDetails />}
                    />

                    <Route path="help" element={<Help />} />
                  </Route>
                </Routes>
              </div>
            </Router>
          </FieldTooltipProvider>
        </HelpProvider>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
